import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../components/AuthProvider';
import { LayoutContext } from '../components/LayoutProvider';
import { authenticate } from '../tools-network';
import { showTemporaryMessage } from '../components/Message';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import NeumorphicContainer from '../components/NeumorphicContainer';

const Login = () => {
	const { t } = useTranslation();
	const { onLogin } = React.useContext(AuthContext);
	const { setIsHeaderVisible, setIsFooterVisible } = React.useContext(LayoutContext);
	const navigate = useNavigate();
	const location = useLocation();
	const [loginEnabled, setLoginEnabled] = useState(true);

	const queryParams = new URLSearchParams(location.search);
	const pending_token = queryParams.get('pending_token');

	useEffect(() => {
		if (pending_token) {
			setIsHeaderVisible(false);
			setIsFooterVisible(false);
		}

		return () => {
			setIsHeaderVisible(true);
			setIsFooterVisible(true);
		};
	}, [pending_token, setIsHeaderVisible, setIsFooterVisible]);

	async function submitLogin(e) {
		e.preventDefault();
		const formData = new FormData(e.target);
		const jsonData = Object.fromEntries(formData.entries());
		const username = jsonData.name;
		const password = jsonData.password;

		setLoginEnabled(false);

		try {
			const res = await authenticate(username, password);
			if (res.status) {
				const loginToken = res.data.token;
				const user_id = res.data.id;
				const user_type = res.data.type;
				const user_name = res.data.name;

				showTemporaryMessage(t("login_succeeded"), t("login_token_received") + loginToken.substring(0, 20) + '...');

				onLogin(loginToken, user_id, user_type, user_name);
				if (pending_token) {
					navigate("/register_qr_code?pending_token=" + pending_token);
				} else if (user_type === 'PRINTER') {
					navigate("/printer");
				} else if (user_type === 'REGULAR') {
					navigate("/bearers");
				} else {
					navigate("/home");
				}
			} else {
				showTemporaryMessage(t("login_failed"), res.msg, 'danger');
				setLoginEnabled(true);
			}
		} catch (err) {
			showTemporaryMessage(t("login_failed"), err.message, 'danger');
			setLoginEnabled(true);
		}
	}

	return (
		<div className="flex flex-col items-center justify-center w-full bg-gradient">
			<NeumorphicContainer>
				<h2 className="text-center font-bold text-2xl">
					{pending_token ? t("please_login_to_register_your_qr_code") : (t("admin_login") || "Admin Login")}
				</h2>

				<form onSubmit={submitLogin} className="mt-5 sm:w-full" id="login-form">
					{/* Username */}
					<div className="flex w-full items-center mt-2">
						<label htmlFor="name" className="sr-only">{t("name")}</label>
						<input
							disabled={!loginEnabled}
							type="text"
							id="name"
							placeholder={t("name")}
							name="name"
							autoComplete="username"
							className="block w-full rounded-md border border-gray-300 px-2 py-1.5 text-gray-900 shadow-sm focus:outline-none focus:ring focus:ring-cyan-600 focus:border-cyan-600 placeholder-gray-400 sm:text-sm sm:leading-6"
							required
						/>
					</div>

					{/* Password */}
					<div className="flex w-full items-center mt-2">
						<label htmlFor="password" className="sr-only">Password:</label>
						<input
							disabled={!loginEnabled}
							type="password"
							id="password"
							placeholder={t("enter_password")}
							name="password"
							autoComplete="current-password"
							className="block w-full rounded-md border border-gray-300 px-2 py-1.5 text-gray-900 shadow-sm focus:outline-none focus:ring focus:ring-cyan-600 focus:border-cyan-600 placeholder-gray-400 sm:text-sm sm:leading-6"
							required
						/>
					</div>

					{/* Submit */}
					<button
						disabled={!loginEnabled}
						type="submit"
						id="button-submit-login"
						className="my-3 btn-main text-white px-4 py-2 w-full rounded-md text-center font-semibold text-lg transition ease-in-out hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{loginEnabled ? t("login") : (
							<span className="flex items-center justify-center gap-2">
								<svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
									<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
									<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
								</svg>
								{t("login")}...
							</span>
						)}
					</button>
				</form>
			</NeumorphicContainer>
			<div className="mt-8 flex items-center gap-3 w-full max-w-xs sm:max-w-sm px-2">
				<div className="flex-1 h-px bg-gray-300" />
				<span className="text-xs text-gray-400 uppercase tracking-wide">{t("or") || "eller"}</span>
				<div className="flex-1 h-px bg-gray-300" />
			</div>
			<Link
				to={pending_token ? `/login?pending_token=${pending_token}` : "/login"}
				className="mt-3 text-sm text-gray-500 hover:text-gray-700 transition flex items-center gap-1.5"
			>
				<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
				{t("login_with_bankid")}
			</Link>
		</div>
	);
};

export default Login;
